import { useState, useEffect, useCallback, useRef } from 'react';
import * as faceapi from 'face-api.js';

interface UseFaceDetectionOptions {
  modelUrl?: string;
}

interface FaceDetectionResult {
  detected: boolean;
  descriptor: Float32Array | null;
  box?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

export function useFaceDetection(options: UseFaceDetectionOptions = {}) {
  const { modelUrl = '/models' } = options;
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Load face-api.js models
  useEffect(() => {
    const loadModels = async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log('Loading face detection models...');

        await Promise.all([
          faceapi.nets.ssdMobilenetv1.loadFromUri(modelUrl),
          faceapi.nets.faceLandmark68Net.loadFromUri(modelUrl),
          faceapi.nets.faceRecognitionNet.loadFromUri(modelUrl)
        ]);

        console.log('Face detection models loaded successfully');
        setIsModelLoaded(true);
      } catch (err) {
        console.error('Error loading face detection models:', err);
        setError('Failed to load face detection models. Please refresh the page.');
      } finally {
        setIsLoading(false);
      }
    };

    loadModels();
  }, [modelUrl]);

  // Start camera
  const startCamera = useCallback(async () => {
    try {
      if (streamRef.current) {
        return; // Camera already started
      }

      const constraints = {
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user' // Front camera
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      return stream;
    } catch (err) {
      console.error('Error starting camera:', err);
      if ((err as Error).name === 'NotAllowedError') {
        setError('Camera permission denied. Please allow camera access to use attendance.');
      } else if ((err as Error).name === 'NotFoundError') {
        setError('No camera found. Please connect a camera to use attendance.');
      } else {
        setError('Failed to start camera. Please check your camera settings.');
      }
      throw err;
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // Detect face and get embedding
  const detectFace = useCallback(async (): Promise<FaceDetectionResult> => {
    if (!isModelLoaded || !videoRef.current) {
      return { detected: false, descriptor: null };
    }

    try {
      const detection = await faceapi
        .detectSingleFace(videoRef.current)
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (!detection) {
        return { detected: false, descriptor: null };
      }

      // Draw detection box on canvas if available
      if (canvasRef.current) {
        const displaySize = {
          width: videoRef.current.videoWidth,
          height: videoRef.current.videoHeight
        };
        faceapi.matchDimensions(canvasRef.current, displaySize);
        const resizedDetection = faceapi.resizeResults(detection, displaySize);

        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          faceapi.draw.drawDetections(canvasRef.current, resizedDetection);
          faceapi.draw.drawFaceLandmarks(canvasRef.current, resizedDetection);
        }
      }

      return {
        detected: true,
        descriptor: detection.descriptor,
        box: {
          x: detection.detection.box.x,
          y: detection.detection.box.y,
          width: detection.detection.box.width,
          height: detection.detection.box.height
        }
      };
    } catch (err) {
      console.error('Error detecting face:', err);
      return { detected: false, descriptor: null };
    }
  }, [isModelLoaded]);

  // Convert Float32Array to regular array for API
  const getEmbeddingArray = useCallback((descriptor: Float32Array): number[] => {
    return Array.from(descriptor);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  return {
    isModelLoaded,
    isLoading,
    error,
    videoRef,
    canvasRef,
    startCamera,
    stopCamera,
    detectFace,
    getEmbeddingArray
  };
}
